import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { toast } from 'react-toastify';
import BackButton from '../../components/BackButton';
import { resolvePaymentsFileUrl } from '../../api/httpClientPayments';
import {
  approvePayment,
  cancelDues,
  deleteDues,
  fetchDuesById,
  markAsPaidByAdmin,
  updateDues,
} from '../../controllers/paymentsController';
import {
  DuesStatus,
  PaymentMethod,
  PaymentMethodNames,
  LogoPostStatusNames,
  canEditDues,
  canMarkPaid,
  getDuesStatusChipSx,
} from './duesConstants';
import { formatDuesDate, formatDuesMoney, toApiDateTime } from './duesFormUtils';

const fieldSx = {
  '& .MuiOutlinedInput-root': { borderRadius: '5px' },
  '& .MuiInputBase-input': { fontSize: '14px' },
};

const DuesDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ amount: '', dueDate: '', finalDueDate: '', description: '' });
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(PaymentMethod.Cash);
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveNotes, setApproveNotes] = useState('');
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchDuesById(id);
      setItem(data);
    } catch (e) {
      setItem(null);
      setError(e?.message || 'Aidat bulunamadı');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const openEdit = () => {
    if (!item) return;
    setEditForm({
      amount: item.amount ?? '',
      dueDate: item.dueDate ? item.dueDate.slice(0, 10) : '',
      finalDueDate: item.finalDueDate ? item.finalDueDate.slice(0, 10) : '',
      description: item.description || '',
    });
    setEditOpen(true);
  };

  const handleUpdate = async () => {
    setActionLoading(true);
    try {
      await updateDues({
        id: item.id,
        amount: Number(editForm.amount),
        dueDate: toApiDateTime(editForm.dueDate),
        finalDueDate: editForm.finalDueDate ? toApiDateTime(editForm.finalDueDate) : null,
        description: editForm.description?.trim() || null,
      });
      toast.success('Aidat güncellendi');
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error(e?.message || 'Güncellenemedi');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkPaid = async () => {
    setActionLoading(true);
    try {
      await markAsPaidByAdmin({
        duesId: item.id,
        paymentMethod,
        paymentDate: new Date().toISOString(),
        description: null,
      });
      toast.success('Ödendi olarak işaretlendi');
      setPaymentOpen(false);
      load();
    } catch (e) {
      toast.error(e?.message || 'İşaretlenemedi');
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = async () => {
    setActionLoading(true);
    try {
      await approvePayment({
        duesId: item.id,
        paymentDate: new Date().toISOString(),
        notes: approveNotes?.trim() || null,
      });
      toast.success('Ödeme onaylandı');
      setApproveOpen(false);
      load();
    } catch (e) {
      toast.error(e?.message || 'Onaylanamadı');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    setActionLoading(true);
    try {
      await cancelDues({ duesId: item.id, cancellationReason: cancelReason?.trim() || null });
      toast.success('Aidat iptal edildi');
      setCancelOpen(false);
      load();
    } catch (e) {
      toast.error(e?.message || 'İptal edilemedi');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    setActionLoading(true);
    try {
      await deleteDues(item.id);
      toast.success('Aidat silindi');
      navigate('/payments/dues');
    } catch (e) {
      toast.error(e?.message || 'Silinemedi');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !item) {
    return (
      <Stack spacing={2}>
        <BackButton to="/payments/dues" />
        <Alert severity="error">{error || 'Aidat bulunamadı'}</Alert>
      </Stack>
    );
  }

  const statusChip = getDuesStatusChipSx(item.status);
  const logoError = item.logoPostStatus === 3 && item.logoPostError;
  const proofUrl = item.paymentProofUrl ? resolvePaymentsFileUrl(item.paymentProofUrl) : null;

  return (
    <Stack spacing={2}>
      <BackButton to="/payments/dues" />
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
        <Box>
          <Typography sx={{ fontSize: '20px', fontWeight: 600, color: '#444' }}>
            {item.memberName || 'Aidat'}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }} flexWrap="wrap">
            <Chip label={item.statusName || 'Durum'} size="small" sx={{ ...statusChip, fontWeight: 600 }} />
            <Typography sx={{ fontSize: '14px', color: '#666' }}>Dönem: {item.period}</Typography>
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#0E4D94' }}>
              {formatDuesMoney(item.amount, item.currency)}
            </Typography>
          </Stack>
          {item.memberId && (
            <Button
              size="small"
              onClick={() => navigate(`/members/${item.memberId}`)}
              sx={{ mt: 1, textTransform: 'none', px: 0 }}
            >
              Üye detayına git
            </Button>
          )}
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {canEditDues(item.status) && (
            <Button variant="outlined" startIcon={<EditIcon />} onClick={openEdit} sx={{ textTransform: 'none' }}>
              Düzenle
            </Button>
          )}
          {canMarkPaid(item.status) && item.status !== DuesStatus.AwaitingApproval && (
            <Button variant="outlined" startIcon={<CheckCircleIcon />} onClick={() => setPaymentOpen(true)} sx={{ textTransform: 'none' }}>
              Nakit Öde
            </Button>
          )}
          {item.status === DuesStatus.AwaitingApproval && (
            <Button variant="contained" startIcon={<CheckCircleIcon />} onClick={() => setApproveOpen(true)} sx={{ textTransform: 'none', bgcolor: '#0E4D94' }}>
              Onayla
            </Button>
          )}
          {canEditDues(item.status) && (
            <Button variant="outlined" color="warning" startIcon={<CancelIcon />} onClick={() => setCancelOpen(true)} sx={{ textTransform: 'none' }}>
              İptal
            </Button>
          )}
          {item.status !== DuesStatus.Paid && (
            <Button variant="outlined" color="error" startIcon={<DeleteOutlineIcon />} onClick={() => setDeleteOpen(true)} sx={{ textTransform: 'none' }}>
              Sil
            </Button>
          )}
        </Stack>
      </Stack>

      {item.isOverdue && (
        <Alert severity="warning">
          Gecikmiş aidat — {item.daysOverdue || 0} gün gecikme
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ boxShadow: 'none', borderColor: '#ececec' }}>
            <CardContent>
              <Typography sx={{ fontSize: '15px', fontWeight: 600, mb: 2 }}>Tarihler</Typography>
              <Stack spacing={1}>
                <Row label="Vade" value={formatDuesDate(item.dueDate)} />
                <Row label="Son ödeme" value={formatDuesDate(item.finalDueDate)} />
                <Row label="Ödeme tarihi" value={formatDuesDate(item.paymentDate)} />
                <Row label="Politika" value={item.policyName || '-'} />
                <Row label="Açıklama" value={item.description || '-'} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ boxShadow: 'none', borderColor: '#ececec' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <AccountBalanceIcon sx={{ color: '#0E4D94' }} />
                <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>Logo borç fişi</Typography>
              </Stack>
              <Stack spacing={1}>
                <Row label="Fiş no" value={item.logoSlipNo || '-'} />
                <Row label="Cari kod" value={item.logoDebtArpCode || '-'} />
                <Row label="Durum" value={item.logoPostStatusName || LogoPostStatusNames[item.logoPostStatus] || '-'} />
                {logoError && <Alert severity="error" sx={{ mt: 1 }}>{item.logoPostError}</Alert>}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        {(item.isPaid || item.status === DuesStatus.AwaitingApproval) && (
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ boxShadow: 'none', borderColor: '#ececec' }}>
              <CardContent>
                <Typography sx={{ fontSize: '15px', fontWeight: 600, mb: 2 }}>Ödeme bilgisi</Typography>
                <Stack spacing={1}>
                  <Row label="Yöntem" value={item.paymentMethodName || PaymentMethodNames[item.paymentMethod] || '-'} />
                  <Row label="Referans" value={item.referenceNumber || '-'} />
                  {proofUrl && (
                    <Typography sx={{ fontSize: '14px' }}>
                      <Box component="a" href={proofUrl} target="_blank" rel="noopener noreferrer" sx={{ color: '#0E4D94' }}>
                        Ödeme kanıtını görüntüle
                      </Box>
                    </Typography>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Edit */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', pr: 3 }}>
          Aidatı Düzenle
          <IconButton onClick={() => setEditOpen(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Tutar" type="number" value={editForm.amount} onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })} fullWidth sx={fieldSx} />
            <TextField label="Vade" type="date" value={editForm.dueDate} onChange={(e) => setEditForm({ ...editForm, dueDate: e.target.value })} InputLabelProps={{ shrink: true }} fullWidth sx={fieldSx} />
            <TextField label="Son ödeme" type="date" value={editForm.finalDueDate} onChange={(e) => setEditForm({ ...editForm, finalDueDate: e.target.value })} InputLabelProps={{ shrink: true }} fullWidth sx={fieldSx} />
            <TextField label="Açıklama" value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} multiline rows={2} fullWidth sx={fieldSx} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} sx={{ textTransform: 'none' }}>İptal</Button>
          <Button variant="contained" onClick={handleUpdate} disabled={actionLoading} sx={{ textTransform: 'none', bgcolor: '#0E4D94' }}>Kaydet</Button>
        </DialogActions>
      </Dialog>

      {/* Mark paid */}
      <Dialog open={paymentOpen} onClose={() => setPaymentOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Nakit Öde</DialogTitle>
        <DialogContent>
          <TextField select label="Ödeme yöntemi" value={paymentMethod} onChange={(e) => setPaymentMethod(Number(e.target.value))} fullWidth sx={{ mt: 1, ...fieldSx }}>
            {Object.entries(PaymentMethodNames).map(([v, l]) => (
              <MenuItem key={v} value={Number(v)}>{l}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentOpen(false)} sx={{ textTransform: 'none' }}>İptal</Button>
          <Button variant="contained" onClick={handleMarkPaid} disabled={actionLoading} sx={{ textTransform: 'none', bgcolor: '#0E4D94' }}>Onayla</Button>
        </DialogActions>
      </Dialog>

      {/* Approve */}
      <Dialog open={approveOpen} onClose={() => setApproveOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Ödemeyi Onayla</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>Ödeme kanıtını kontrol ettiniz mi?</Alert>
          <TextField label="Not" value={approveNotes} onChange={(e) => setApproveNotes(e.target.value)} multiline rows={2} fullWidth sx={fieldSx} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveOpen(false)} sx={{ textTransform: 'none' }}>İptal</Button>
          <Button variant="contained" onClick={handleApprove} disabled={actionLoading} sx={{ textTransform: 'none', bgcolor: '#0E4D94' }}>Onayla</Button>
        </DialogActions>
      </Dialog>

      {/* Cancel */}
      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Aidatı İptal Et</DialogTitle>
        <DialogContent>
          <TextField label="İptal nedeni" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} multiline rows={3} fullWidth sx={{ mt: 1, ...fieldSx }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)} sx={{ textTransform: 'none' }}>Vazgeç</Button>
          <Button variant="contained" color="warning" onClick={handleCancel} disabled={actionLoading} sx={{ textTransform: 'none' }}>İptal Et</Button>
        </DialogActions>
      </Dialog>

      {/* Delete */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Aidatı Sil</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '14px' }}>Bu aidat kaydını silmek istediğinize emin misiniz?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} sx={{ textTransform: 'none' }}>Vazgeç</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={actionLoading} sx={{ textTransform: 'none' }}>Sil</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

const Row = ({ label, value }) => (
  <Stack direction="row" spacing={1}>
    <Typography sx={{ fontSize: '13px', color: '#a3a3a3', minWidth: 110 }}>{label}:</Typography>
    <Typography sx={{ fontSize: '14px', color: '#444', fontWeight: 500 }}>{value}</Typography>
  </Stack>
);

export default DuesDetail;
